import React, { type CSSProperties, forwardRef, ReactNode } from 'react';
import { mergeProps, useButton, useFocusRing } from 'react-aria';
import { genericMemo } from '@direct-frontend/stdlib';
import { getPlatformStyles } from '@direct-frontend/platform-code';

import { TestProps } from '../../internal/types/testTypes';
import { DataAttributesProps } from '../../internal/types/dataAttributesTypes';
import { cn } from '../../lib/css/className/className';
import { useObjectRef } from '../../hooks/useObjectRef';
import { LayoutComponentsLayoutProps, useLayoutStyle } from '../../lib/layoutStyle/layoutStyle';

getPlatformStyles({
    common: import('./Card.css'),
    mobile: import('./Card@mobile.css'),
});

const cls = cn('Card');

export type CardColor =
    | 'white'
    | 'white-2'
    | 'gray'
    | 'gray-2'
    | 'gray-3'
    | 'gray-4'
    | 'gray-5'
    | 'red'
    | 'red-2'
    | 'red-3'
    | 'green'
    | 'green-2'
    | 'green-3'
    | 'blue'
    | 'blue-2'
    | 'blue-3'
    | 'purple'
    | 'purple-2'
    | 'purple-3'
    | 'orange'
    | 'yellow'
    | 'yellow-2'
    | 'yellow-3'
    | 'yellow-4'
    | 'black'
    | 'transparent'
    | 'strict-black'
    | 'strict-gray'
    | 'strict-white'
    | 'strict-white-2'
    | 'ai-high-contrast';

export type BorderColor = 'gray' | 'red' | 'purple';
export type CardPadding = '8' | '12' | '14' | '16' | '20' | '24' | '28' | '36' | '48' | '64';
export type CardBorderRadius = '4' | '8' | '10' | '12' | '14' | '20' | '24' | '28' | '32' | '44';
export type CardPaddingCorrection = 'top' | 'top-bottom';

export interface CardProps extends TestProps, DataAttributesProps, LayoutComponentsLayoutProps {
    /**
     * Дочерние элементы
     */
    children: ReactNode;

    /**
     * Цвет фона элемента
     * @default transparent
     */
    color?: CardColor | 'current';

    /**
     * Цвет обводки элемента
     * @default none
     */
    borderColor?: BorderColor;

    /**
     * Отступ со всех сторон элемента (рекомендуется устанавливать тот, что слева/справа в макете).
     * @default none
     */
    padding?: CardPadding;

    /**
     * Корректировка отступов сверху и снизу.
     * Используется, когда требуется сделать верхний либо верхний и нижний отступ меньше, чем по бокам.
     */
    paddingCorrection?: CardPaddingCorrection;

    /**
     * Скругление углов элемента
     * @default none
     */
    borderRadius?: CardBorderRadius;

    /**
     * Корректировка скругления верхних углов
     * @default false
     */
    topBorderRadiusCorrection?: boolean;

    /**
     * Корректировка скругления нижних углов
     * @default false
     */
    bottomBorderRadiusCorrection?: boolean;

    /**
     * Названия классов для элемента.
     * Для минимизации нод при выравнивании можно миксовать с классами из хуков `useVStack` и `useHStack`.
     */
    className?: string;

    /**
     * Обработчик нажатия на элемент
     */
    onClick?: () => void;

    /**
     * Атрибут id для элемента
     */
    id?: string;

    /**
     * Признак отключения взаимодействия с Card
     */
    isDisabled?: boolean;
}

/**
 * Компонент для создания контейнеров с заданными отступами, цветом фона, обводки и скруглением углов.
 */
export const Card = genericMemo(
    forwardRef<HTMLDivElement, CardProps>(function Card(props, forwardedRef) {
        const {
            className,
            children,
            color = 'transparent',
            borderColor,
            padding,
            paddingCorrection,
            borderRadius,
            topBorderRadiusCorrection = false,
            bottomBorderRadiusCorrection = false,
            id,
            isDisabled,
            onClick,
            'data-testid': dataTestId,

            width,
            minWidth,
            maxWidth,
            height,
            minHeight,
            maxHeight,
            flex,

            ...otherProps
        } = props;

        const ref = useObjectRef(forwardedRef);
        const { buttonProps, isPressed } = useButton({ onPress: onClick, elementType: 'div', isDisabled }, ref);
        const { focusProps, isFocusVisible } = useFocusRing();
        const isInteractive = Boolean(onClick) && !isDisabled;

        const { style: layoutStyle, className: layoutClassName } = useLayoutStyle({
            width,
            minWidth,
            maxWidth,
            height,
            minHeight,
            maxHeight,
            flex,
        });

        // Проп style не описан в CardProps, но исторически доезжает до DOM через ...otherProps.
        // Читаем его явно, чтобы смержить с лэйаутными стилями, а не затереть целиком.
        // Исправить при редактировании. Автоматический игнор, см. DIRECT-193196.
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
        const { style: outerStyle } = props as CardProps & { style?: CSSProperties };

        return (
            <div
                {...(onClick && mergeProps(buttonProps, focusProps))}
                {...otherProps}
                className={cls(
                    {
                        color,
                        borderColor,
                        padding,
                        paddingCorrection,
                        borderRadius,
                        topBorderRadiusCorrection,
                        bottomBorderRadiusCorrection,
                        focused: isFocusVisible,
                        interactive: isInteractive,
                        pressed: isPressed,
                    },
                    [className, layoutClassName],
                )}
                style={outerStyle ? { ...layoutStyle, ...outerStyle } : layoutStyle}
                data-testid={dataTestId}
                id={id}
                ref={ref}
            >
                {children}
            </div>
        );
    }),
);
